import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuthStore } from "@/features/auth";
import { useRolesPermissionsStore } from "./useRolesPermissionsStore";
import { userService } from "../services";
import {
  ADMIN_ROLES,
  CLIENT_ROLE_GROUPS,
  buildRoleItems,
  filterModulesByAudience,
  normalizeId,
  normalizeRoleKey,
} from "../utils/roles-permissions";
import { showToast } from "@/lib/utils/toast";

export const useRolesPermissionsPageData = () => {
  const { userRole, user } = useAuthStore();
  const {
    roles,
    permissionsModules,
    selectedRole,
    dialogOpen,
    dialogMode,
    deleteDialogOpen,
    loadingRoles,
    creatingRole,
    updatingRole,
    deletingRoleId,
    fetchRoleList,
    fetchPermissionModules,
    openCreateDialog,
    openEditDialog,
    openDeleteDialog,
    closeDeleteDialog,
    closeDialog,
    setDialogOpen,
    createRole,
    updateRole,
    deleteRole,
  } = useRolesPermissionsStore();
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [roleUsageById, setRoleUsageById] = useState({});
  const [roleUsageLoading, setRoleUsageLoading] = useState(false);

  const permissions = user?.permissions || [];

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const canViewRoles = hasPermission("roles_permissions:view");

  const isAdminGroup = useMemo(() => {
    const role = normalizeRoleKey(userRole);
    return ADMIN_ROLES.map((item) => normalizeRoleKey(item)).includes(role);
  }, [userRole]);

  const isSuperAdmin = useMemo(() => {
    const role = normalizeRoleKey(userRole);
    return role === "super_admin";
  }, [userRole]);

  const companyId = useMemo(
    () => normalizeId(user?.companyId),
    [user?.companyId],
  );

  const currentRoleGroup = useMemo(() => {
    const currentRoleKey = normalizeRoleKey(userRole);
    if (!currentRoleKey) return "";
    const roleMatch = roles.find((role) => {
      const roleName =
        role?.name || role?.title || role?.key || role?.role || "";
      return normalizeRoleKey(roleName) === currentRoleKey;
    });
    return normalizeRoleKey(roleMatch?.group || currentRoleKey);
  }, [roles, userRole]);

  const moduleSource =
    moduleCatalog.length > 0 ? moduleCatalog : permissionsModules;
  const moduleAudience = CLIENT_ROLE_GROUPS.has(currentRoleGroup) ? "user" : "";

  const filteredModules = useMemo(() => {
    if (!moduleAudience) return moduleSource;
    return filterModulesByAudience(moduleSource, moduleAudience);
  }, [moduleAudience, moduleSource]);

  const roleItems = useMemo(
    () => buildRoleItems(roles, moduleSource),
    [moduleSource, roles],
  );

  const visibleRoleItems = useMemo(() => {
    if (isAdminGroup) {
      return roleItems.filter((item) => !normalizeId(item?.raw?.companyId));
    }
    if (!companyId) return [];
    const roles = roleItems.filter(
      (item) => normalizeId(item?.raw?.companyId) === companyId,
    );

    if (isSuperAdmin) {
      const superAdminRole = roleItems.find(
        (item) => item?.role === "super_admin",
      );

      return superAdminRole ? [superAdminRole, ...roles] : roles;
    }

    return roles;
  }, [companyId, isAdminGroup, isSuperAdmin, roleItems]);

  useEffect(() => {
    if (!canViewRoles) return;
    fetchRoleList();
  }, [fetchRoleList]);

  useEffect(() => {
    if (!canViewRoles) return;
    let isActive = true;
    const loadModules = async () => {
      if (!permissionsModules.length) {
        const success = await fetchPermissionModules({ audience: "all" });
        if (!success && isActive) {
          setModuleCatalog([]);
        }
        return;
      }
      if (isActive) {
        setModuleCatalog(permissionsModules);
      }
    };

    loadModules();

    return () => {
      isActive = false;
    };
  }, [fetchPermissionModules, permissionsModules]);

  useEffect(() => {
    if (isAdminGroup || !companyId) {
      setRoleUsageById({});
      return;
    }

    const roleIds = visibleRoleItems
      .map((item) =>
        normalizeId(
          item?.raw?._id || item?.raw?.id || item?.raw?.key || item?.raw?.role,
        ),
      )
      .filter(Boolean);

    if (roleIds.length === 0) {
      setRoleUsageById({});
      return;
    }

    let isActive = true;
    setRoleUsageLoading(true);

    const loadUsage = async () => {
      const results = await Promise.all(
        roleIds.map(async (roleId) => {
          try {
            const data = await userService.listUsers({
              roleId,
              limit: 1,
              page: 1,
            });
            if (!data) {
              return [roleId, false];
            }
            const payload = data?.data || data || {};
            const items = payload?.items || payload?.data?.items || [];
            return [roleId, Array.isArray(items) && items.length > 0];
          } catch (_error) {
            return [roleId, false];
          }
        }),
      );

      if (!isActive) return;
      const nextMap = results.reduce((acc, [roleId, used]) => {
        acc[roleId] = used;
        return acc;
      }, {});
      setRoleUsageById(nextMap);
    };

    loadUsage().finally(() => {
      if (isActive) setRoleUsageLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [companyId, isAdminGroup, visibleRoleItems]);

  const handleSaveRole = useCallback(
    async (payload, options = {}) => {
      const roleIdFromPayload = payload?.id;
      const roleId =
        roleIdFromPayload ||
        selectedRole?._id ||
        selectedRole?.id ||
        selectedRole?.key ||
        selectedRole?.role ||
        null;
      const { showToast: shouldToast = true, toastMessages = {} } = options;
      if (roleId) {
        try {
          const updateRolePromise = updateRole(roleId, payload);
          if (shouldToast) {
            showToast.promise(updateRolePromise, {
              loading: "Updating role...",
              success: "Role updated successfully.",
              error: (err) => err.message,
              ...toastMessages,
            });
          }
          await updateRolePromise;
          closeDialog();
        } catch (_error) {
          return;
        }
        return;
      }

      try {
        const createRolePromise = createRole(payload);
        if (shouldToast) {
          showToast.promise(createRolePromise, {
            loading: "Creating role...",
            success: "Role created successfully.",
            error: (err) => err.message,
            ...toastMessages,
          });
        }
        await createRolePromise;

        closeDialog();
      } catch (_error) {
        return;
      }
    },
    [closeDialog, createRole, selectedRole, updateRole],
  );

  const handleConfirmDelete = useCallback(
    async (options = {}) => {
      const targetRole = selectedRole;
      const roleId =
        targetRole?._id ||
        targetRole?.id ||
        targetRole?.key ||
        targetRole?.role ||
        null;
      const { showToast: shouldToast = true, toastMessages = {} } = options;
      if (!roleId) {
        closeDeleteDialog();
        return;
      }
      try {
        const deleteRolePromise = deleteRole(roleId);
        if (shouldToast) {
          showToast.promise(deleteRolePromise, {
            loading: "Deleting role...",
            success: "Role deleted successfully.",
            error: (err) => err.message,
            ...toastMessages,
          });
        }
        await deleteRolePromise;
      } finally {
        closeDeleteDialog();
      }
    },
    [closeDeleteDialog, deleteRole, selectedRole],
  );

  return {
    roles,
    dialogOpen,
    dialogMode,
    deleteDialogOpen,
    loadingRoles,
    creatingRole,
    updatingRole,
    deletingRoleId,
    selectedRole,
    isAdminGroup,
    companyId,
    filteredModules,
    visibleRoleItems,
    roleUsageById,
    roleUsageLoading,
    openCreateDialog,
    openEditDialog,
    openDeleteDialog,
    closeDeleteDialog,
    closeDialog,
    setDialogOpen,
    handleSaveRole,
    handleConfirmDelete,
  };
};
